import {
  createFeatureSelector,
  createSelector
} from '@ngrx/store';

import * as rootReducer from '../../app.reducers';
import { getRouterParams } from '../../app.reducers';
import { RequestPayload } from '../../shared/common/models/request-payload';
import { PaginationInfoInterface } from '../../shared/common/interfaces/pagination-info.interface';
import { Supplier } from '../model';
import { SupplierItemsStateInterface } from './items/supplier-items-state.interface';
import { queryConfig } from './query-config/query-config.reducer';
import { searchResultIdentifiers } from './search-result-identifiers/search-result-identifiers.reducer';
import { paginationInfo } from './pagination-info/pagination-info.reducer';
import { items } from './items/items.reducer';
import { fetchHasLeadsInProgress } from './fetch-has-leads-in-progress/fetch-has-leads-in-progress.reducer';
import { fetchMultiInProgress } from './fetch-multi-in-progress/fetch-multi-in-progress.reducer';
import { fetchSingleInProgress } from './fetch-single-in-progress/fetch-single-in-progress.reducer';
import { fetchMultiError } from './fetch-multi-error/fetch-multi-error.reducer';
import { fetchSingleError } from './fetch-single-error/fetch-single-error.reducer';
import { orderingValueRange } from './ordering-value-range/ordering-value-range.reducer';
import { tempOrderingValue } from './temp-ordering-value/temp-ordering-value.reducer';


/**
 * Supplier State Interface
 */
export interface SupplierStateInterface {
  queryConfig: RequestPayload;
  searchResultIdentifiers: Supplier[];
  paginationInfo: PaginationInfoInterface;
  items: SupplierItemsStateInterface;
  fetchHasLeadsInProgress: boolean;
  fetchMultiInProgress: boolean;
  fetchSingleInProgress: boolean;
  fetchSingleError: any;
  fetchMultiError: any;
  orderingValueRange: {label: string; value: string}[];
  tempOrderingValue: string[];
}

export interface StateInterface extends rootReducer.StateInterface {
  supplier: SupplierStateInterface;
}

/**
 * Supplier State Reducer
 */
export const reducers = {
  queryConfig,
  searchResultIdentifiers,
  paginationInfo,
  items,
  fetchHasLeadsInProgress,
  fetchMultiInProgress,
  fetchSingleInProgress,
  fetchMultiError,
  fetchSingleError,
  orderingValueRange,
  tempOrderingValue
};

export const getSupplierState = createFeatureSelector<SupplierStateInterface>('supplier');

export const getQueryConfig = createSelector(getSupplierState, (state: SupplierStateInterface) => state.queryConfig);
export const getSearchResultIdentifiers = createSelector(getSupplierState, (state: SupplierStateInterface) => state.searchResultIdentifiers);
export const getPaginationInfo = createSelector(getSupplierState, (state: SupplierStateInterface) => state.paginationInfo);
export const getItems = createSelector(getSupplierState, (state: SupplierStateInterface) => state?.items);
export const getFetchHasLeadsInProgress = createSelector(getSupplierState, (state: SupplierStateInterface) => state.fetchHasLeadsInProgress);
export const getFetchMultiInProgress = createSelector(getSupplierState, (state: SupplierStateInterface) => state.fetchMultiInProgress);
export const getFetchSingleInProgress = createSelector(getSupplierState, (state: SupplierStateInterface) => state.fetchSingleInProgress);
export const getFetchMultiError = createSelector(getSupplierState, (state: SupplierStateInterface) => state.fetchMultiError);
export const getFetchSingleError = createSelector(getSupplierState, (state: SupplierStateInterface) => state.fetchSingleError);
export const getOrderingValueRange = createSelector(getSupplierState, (state: SupplierStateInterface) => state.orderingValueRange);
export const getTempOrderingValue = createSelector(getSupplierState, (state: SupplierStateInterface) => state.tempOrderingValue);
export const getSelectedItemUuidOrSlug = createSelector(getRouterParams, (_routerParams) => _routerParams && _routerParams.selectedSupplierUuidOrSlug);


/**
 * Returns the current selected Item
 *
 * @type {OutputSelector<State, string, (res: any) => string>}
 */
export const getSelectedItem = createSelector(getItems, getSelectedItemUuidOrSlug,
  (_items: SupplierItemsStateInterface, _selectedItemUuidOrSlug: string) => {
    if (_items && _selectedItemUuidOrSlug) {
      return _items[_selectedItemUuidOrSlug] || Object.keys(_items)
        .map(uuid => _items[uuid])
        .find(supplier => Array.isArray(supplier.slugs) && supplier.slugs.includes(_selectedItemUuidOrSlug));
    } else {
      return undefined;
    }
  });

export const getSupplierWithDetailsLoaded = createSelector(getItems, (_items: SupplierItemsStateInterface, props: {uuidOrSlug: string}) => {
  if (_items) {
    return Object.keys(_items).map(uuid => _items[uuid])
      .filter(supplier => supplier.detailsLoaded)
      .find(supplier => supplier.uuid === props.uuidOrSlug || Array.isArray(supplier.slugs) && supplier.slugs.includes(props.uuidOrSlug));
  } else {
    return undefined;
  }
});

export const getSelectedItemUuid = createSelector(getSelectedItem, (_item) => _item ? _item.uuid : undefined);

export const getSupplierByUuid = createSelector(getItems, (_items, props: {uuid: string}) => {
  if (_items && props) {
    return _items[props.uuid];
  }
});

export const getSearchResults = createSelector(getSearchResultIdentifiers, getItems, (_searchResultsIdentifiers, _items) => {
  if (Array.isArray(_searchResultsIdentifiers) && _items) {
    return _searchResultsIdentifiers.map(item => {
      const result: Supplier = {
        ..._items[item.uuid],
        advertised: item.advertised
      };
      return result;
    });
  } else {
    return undefined;
  }
});

export const getSupplierUrlPathSegmentListByUuids = createSelector(getItems, (_items, props: {uuids: []}) => {
  if (_items && props) {
    const suppliers = [];
    props.uuids.forEach(uuid => {
      const loadedSupplier = Object.values(_items).map(supplier => ({
        urlPathSegment: supplier['urlPathSegment'],
        uuid: supplier['uuid']
      })).filter(item => item.uuid === uuid)[0];


      if (loadedSupplier) {
        suppliers.push(loadedSupplier);
      }
    });
    return suppliers;
  }
});
